import React, { useEffect, useState } from "react";
import { Button, Toast, ToastContainer } from "react-bootstrap";
import { CircleCheck, X } from "lucide-react";

import { TablerAlertFilled } from "Components/Icons/AlertTriangle";
import { SquareXFilled } from "Components/Icons/SquaredXFilled";

import type { ToastProps } from "@/contexts/toastContext";
import { useToast } from "@/hooks/useToast";
type ToastType = "success" | "error" | "warning" | "info"

interface CustomToastProps {
    id: string | number;
    title: string;
    message: string;
    type: ToastType;
    delay?: number;
}

interface ToastContainerProps {
    toasts: ToastProps[]
}

const toastTypes = {
    success: {
      icon: <CircleCheck />,
      iconClass: "success-icon",
    },
    warning: {
      icon: <TablerAlertFilled />,
      iconClass: "warning-icon",
    },
    info: {
      icon: <TablerAlertFilled />,
      iconClass: "info-icon",
    },
    error: {
      icon: <SquareXFilled />,
      iconClass: "error-icon",
    },
};

export const ToastWrapper = ({ toasts }: ToastContainerProps) => {
    return (
        <ToastContainer position="top-end" className="p-2">
            {toasts.map((toast) => (
                <CustomToast key={`${toast.id}`} {...toast} />
            ))}
        </ToastContainer>
    )
}

const CustomToast = ({ id, title, message, type = "success", delay = 5000 }: CustomToastProps) => {
    const [show, setShow] = useState<boolean>(false)

    const toast = useToast()

    const { icon, iconClass } = toastTypes[type];

    const onClose = () => {
        setShow(!show)
        toast.remove(id as string)
    }

    useEffect(() => {
        setShow(true);
    }, []);

    return (
        <Toast
            delay={delay}
            show={show}
            onClose={onClose}
            autohide
            className={`custom-toast`}
        >
            <Toast.Body className="toast-content">
                <div className="d-flex align-items-start gap-2">
                    <div className={`icon-wrapper ${iconClass}`}>{icon}</div>
                    <div className="message-text mt-1">
                        <h1 className="mb-0">{title}</h1>
                        <p className="mb-0">{message}</p>
                    </div>
                </div>
                <Button variant="ghost" onClick={onClose} className="p-0">
                    <X role="button" className="close-icon" />
                </Button>
            </Toast.Body>
        </Toast>
    );
};

export default CustomToast;